// =============================================================================
// Email Express — brand-style resolver (brand-agnostic)
// -----------------------------------------------------------------------------
// Turns any brand row into a typed style object the Email Express preview blocks
// consume. This is the ONLY place brand-specific values are resolved: the block
// renderers read the resolved object and stay brand-neutral. Every field has a
// neutral, generic default so a brand missing a labeled palette, a logo, or
// typography hints renders sensibly instead of breaking — and never falls back
// to any one brand's specific values.
// =============================================================================

import {
  selectBrandFontRoles,
  collectBrandFontLoads,
  brandFontCssStack,
  brandFontFamilyName,
  brandFontFormat,
  BRAND_FONT_SANS_FALLBACK,
} from "./brand-fonts";
import { resolveBrandCtaStyleForSurface, ctaShapeRadiusCss } from "./brand-cta-style";

export interface EmailBrandColors {
  /** Hero / strong background. */
  primary: string;
  /** CTA buttons, bullet dots, stars — the brand's pop color. */
  accent: string;
  /** Soft section background (e.g. selling points). */
  background: string;
  /** Default body/heading text color on light surfaces. */
  text: string;
  /** Card / body surface. */
  card: string;
  /** Deep footer background. */
  footer: string;
  /** Readable text color on a primary-colored surface. */
  onPrimary: string;
  /** Readable text color on an accent-colored surface (button label). */
  onAccent: string;
  /** Readable text color on the footer surface. */
  onFooter: string;
}

export interface EmailBrandFonts {
  /** CSS font-family value for headings. */
  heading: string;
  /** CSS font-family value for body copy. */
  body: string;
}

export interface EmailBrandButton {
  backgroundColor: string;
  color: string;
  borderRadius: string;
  /** CSS border shorthand for outline-style CTAs; null ⇒ no border (solid). */
  border: string | null;
  /** "uppercase" when the brand CTA style forces all-caps labels; null ⇒ as-is. */
  textTransform: string | null;
  /** CSS font-family stack for the CTA label; null ⇒ inherit the block's font. */
  fontFamily: string | null;
}

// A single render surface: a section background paired with the foreground text
// colors that are WCAG-legible on it. Every block paints its section from one of
// these so brand color shows up across backgrounds/bands/dividers — not just
// CTAs — and both renderers (preview + MJML) stay in lockstep.
export interface EmailBrandSurface {
  bg: string;
  /** Primary text color, contrast-correct on `bg`. */
  text: string;
  /** Muted/secondary text (labels, captions) still legible on `bg`. */
  muted: string;
  /** Hairline/divider color that reads on `bg`. */
  border: string;
  /** True when `bg` is dark (light text) — drives onDark treatments. */
  dark: boolean;
}

export interface EmailBrandSurfaces {
  /** Soft brand-derived canvas — never pure flat white. */
  page: EmailBrandSurface;
  /** Clean light card surface for primary reading. */
  light: EmailBrandSurface;
  /** Subtle brand-tinted wash (a quiet color band). */
  tinted: EmailBrandSurface;
  /** Strong dark brand band with WCAG-safe light text (the depth/contrast section). */
  dark: EmailBrandSurface;
  /** Accent (pop color) band with contrast-correct text. */
  accent: EmailBrandSurface;
}

// A brand-saved font that heading/body resolved to, carried alongside the
// resolved `fonts` so the render twins (client preview + Satori/MJML send) can
// actually LOAD it: Google families via the stylesheet API, uploads via
// @font-face from their R2 URL. Absent/empty when the brand saved no fonts, so
// the render stays byte-identical to before this layer existed.
export interface EmailBrandFontFace {
  /** Concrete first family in the stack (Google name, or brand-upload-<id>). */
  family: string;
  source: "google" | "upload";
  /** R2 URL for uploads; null for Google (loaded by family name). */
  url: string | null;
  /** CSS @font-face format() hint for uploads (e.g. "woff2"); null otherwise. */
  format: string | null;
}

export interface EmailBrandStyle {
  brandName: string;
  logoUrl: string | null;
  colors: EmailBrandColors;
  fonts: EmailBrandFonts;
  button: EmailBrandButton;
  surfaces: EmailBrandSurfaces;
  /** Brand-saved fonts the heading/body roles use (for loading). */
  fontFaces: EmailBrandFontFace[];
}

// ── Neutral defaults (brand-agnostic) ────────────────────────────────────────
// Deliberately desaturated slate tones — never any single brand's palette.
const DEFAULT_COLORS = {
  primary: "#1E293B", // slate-800
  accent: "#334155", // slate-700
  background: "#F1F5F9", // slate-100
  text: "#0F172A", // slate-900
  card: "#FFFFFF",
  footer: "#0F172A", // slate-900
};

// Clean, ubiquitous sans for both roles when a brand gives no typography hint.
const DEFAULT_HEADING_FONT =
  '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
const DEFAULT_BODY_FONT =
  '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';

const DEFAULT_BUTTON_RADIUS = "8px";

// Fallback tails appended after a brand's SAVED font family. The saved family
// (Google or uploaded) is what actually renders; the tail only shows while it
// loads or in clients that can't load web fonts, so a neutral sans is fine for
// both roles (the brand didn't hand us a serif/sans category with the font).
const HEADING_FALLBACK_TAIL = BRAND_FONT_SANS_FALLBACK;
const BODY_FALLBACK_TAIL = BRAND_FONT_SANS_FALLBACK;

// ── Color helpers ────────────────────────────────────────────────────────────
const isHexColor = (h?: string): boolean =>
  typeof h === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h.trim());

function normalizeHex(h: string): string {
  let v = h.trim();
  if (v.length === 4) {
    // #abc -> #aabbcc
    v = "#" + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
  }
  return v.toLowerCase();
}

// Relative luminance (WCAG) — used to pick black/white text for contrast.
function luminance(hex: string): number {
  const v = normalizeHex(hex);
  const r = parseInt(v.slice(1, 3), 16) / 255;
  const g = parseInt(v.slice(3, 5), 16) / 255;
  const b = parseInt(v.slice(5, 7), 16) / 255;
  const lin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

// Readable foreground (near-black or near-white) for a given background.
function readableOn(hex: string): string {
  return luminance(hex) > 0.5 ? "#1A1A1A" : "#FFFFFF";
}

// WCAG contrast ratio between two colors (1..21).
function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

// ── Color math (surface derivation) ──────────────────────────────────────────
function hexToRgb(hex: string): [number, number, number] {
  const v = normalizeHex(isHexColor(hex) ? hex : "#000000");
  return [
    parseInt(v.slice(1, 3), 16),
    parseInt(v.slice(3, 5), 16),
    parseInt(v.slice(5, 7), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

// Linear blend of two colors. t=0 → a, t=1 → b.
function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

const lighten = (hex: string, t: number) => mix(hex, "#ffffff", t);
const darken = (hex: string, t: number) => mix(hex, "#000000", t);

// Drive a color dark enough that white text clears WCAG AA (4.5:1). Used so the
// guaranteed dark section is always legible even when a brand's "primary" is a
// mid-tone that fails contrast with white on its own.
function ensureDarkBg(hex: string): string {
  let out = isHexColor(hex) ? normalizeHex(hex) : "#1a1a1a";
  let guard = 0;
  while (contrastRatio("#ffffff", out) < 4.5 && guard < 12) {
    out = darken(out, 0.12);
    guard++;
  }
  return out;
}

// ── Palette role matching ────────────────────────────────────────────────────
// Mirrors the LP Express approach: match each role to a labeled colorPalette
// entry by keyword. Order matters — specific roles (deep navy / card) match
// before broader ones (navy / background). Each entry is consumed once.
type PaletteEntry = { label: string; hex: string };

const ROLE_MATCHERS: Array<{
  role: keyof typeof DEFAULT_COLORS;
  match: (label: string) => boolean;
}> = [
  {
    role: "footer",
    match: (l) => l.includes("deep navy") || l.includes("footer"),
  },
  { role: "card", match: (l) => l.includes("card") },
  {
    role: "accent",
    match: (l) =>
      l.includes("accent") ||
      l.includes("coral") ||
      l.includes("secondary") ||
      l.includes("highlight"),
  },
  {
    role: "primary",
    match: (l) => l.includes("primary") || l.includes("navy") || l.includes("brand"),
  },
  {
    role: "text",
    match: (l) =>
      l.includes("text") || l.includes("near-black") || l.includes("near black") || l.includes("ink"),
  },
  {
    role: "background",
    match: (l) => l.includes("background") || l.includes("cream") || l.includes("base"),
  },
];

function readPalette(raw: unknown): PaletteEntry[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Array<{ label?: string; hex?: string }>)
    .map((e) => ({
      label: (e?.label ?? "").toLowerCase().trim(),
      hex: (e?.hex ?? "").trim(),
    }))
    .filter((e) => isHexColor(e.hex));
}

function resolveColors(brand: any): EmailBrandColors {
  const colors: Record<keyof typeof DEFAULT_COLORS, string> = { ...DEFAULT_COLORS };

  // 1) Authoritative source: labeled colorPalette (role keyword matching).
  const entries = readPalette(brand?.colorPalette);
  const used = new Set<number>();
  for (const m of ROLE_MATCHERS) {
    for (let i = 0; i < entries.length; i++) {
      if (used.has(i)) continue;
      if (m.match(entries[i].label)) {
        colors[m.role] = normalizeHex(entries[i].hex);
        used.add(i);
        break;
      }
    }
  }

  // 2) Fallback for any role still at its neutral default: pull from
  //    brandDesignSystem color arrays (unlabeled), then visualRules.palette.
  const ds = brand?.brandDesignSystem ?? {};
  const dsPrimary = (Array.isArray(ds.primaryColors) ? ds.primaryColors : []).filter(isHexColor);
  const dsAccent = (Array.isArray(ds.accentColors) ? ds.accentColors : []).filter(isHexColor);
  const dsSecondary = (Array.isArray(ds.secondaryColors) ? ds.secondaryColors : []).filter(isHexColor);

  const matchedRoles = new Set(
    ROLE_MATCHERS.filter((m) =>
      entries.some((e, i) => used.has(i) && m.match(e.label)),
    ).map((m) => m.role),
  );

  if (!matchedRoles.has("primary") && dsPrimary[0]) colors.primary = normalizeHex(dsPrimary[0]);
  if (!matchedRoles.has("accent") && (dsAccent[0] || dsSecondary[0])) {
    colors.accent = normalizeHex(dsAccent[0] || dsSecondary[0]);
  }

  return {
    ...colors,
    onPrimary: readableOn(colors.primary),
    onAccent: readableOn(colors.accent),
    onFooter: readableOn(colors.footer),
  };
}

// ── Font mapping ─────────────────────────────────────────────────────────────
// Map free-text typography *descriptions* to concrete loadable web fonts. The
// families referenced here must be loaded (see EMAIL_FONT_LINK_HREF). When a
// brand provides no hint we fall back to a neutral clean sans for both roles.
const SERIF_STACK_SUFFIX = ', Georgia, "Times New Roman", serif';
const SANS_STACK_SUFFIX = ', system-ui, -apple-system, "Segoe UI", sans-serif';

function mapHeadingFont(desc: string): string {
  const d = desc.toLowerCase();
  if (!d.trim()) return DEFAULT_HEADING_FONT;
  const wantsSerif = d.includes("serif") && !d.includes("sans-serif") && !d.includes("sans serif");
  if (
    d.includes("slab") ||
    d.includes("sturdy") ||
    d.includes("western") ||
    d.includes("rugged") ||
    d.includes("condensed slab")
  ) {
    return `"Roboto Slab"${SERIF_STACK_SUFFIX}`;
  }
  if (wantsSerif || d.includes("display") || d.includes("elegant") || d.includes("editorial")) {
    return `"Playfair Display"${SERIF_STACK_SUFFIX}`;
  }
  if (d.includes("sans") || d.includes("clean") || d.includes("modern") || d.includes("geometric")) {
    return `"Montserrat"${SANS_STACK_SUFFIX}`;
  }
  return DEFAULT_HEADING_FONT;
}

function mapBodyFont(desc: string): string {
  const d = desc.toLowerCase();
  if (!d.trim()) return DEFAULT_BODY_FONT;
  // "sans" wins for body — many brand descriptions read "serif headings, clean
  // sans body", so a bare "serif" match must not flip the body to a serif face.
  if (d.includes("sans")) return DEFAULT_BODY_FONT;
  if (d.includes("serif")) return `"Lora"${SERIF_STACK_SUFFIX}`;
  return DEFAULT_BODY_FONT;
}

// Pick the first non-empty hint; only fall back to the broader (possibly
// ambiguous) description when the role-specific one is missing.
function firstHint(...hints: unknown[]): string {
  for (const h of hints) {
    if (typeof h === "string" && h.trim()) return h;
  }
  return "";
}

function resolveFonts(brand: any): EmailBrandFonts {
  const ds = brand?.brandDesignSystem ?? {};
  const td = ds.typographyDirection ?? {};
  const vr = brand?.visualRules ?? {};
  const headingHint = firstHint(td.headlineStyle, td.accentStyle, vr.typography);
  const bodyHint = firstHint(td.bodyStyle, vr.typography);
  // A brand's explicitly SAVED fonts (Design System step) win over the
  // descriptive-hint mapping — they're a concrete choice. Fall back to the hint
  // mapping per-role when a role has no saved font.
  const roles = selectBrandFontRoles(ds.fonts);
  return {
    heading: roles.heading
      ? brandFontCssStack(roles.heading, HEADING_FALLBACK_TAIL)
      : mapHeadingFont(headingHint),
    body: roles.body
      ? brandFontCssStack(roles.body, BODY_FALLBACK_TAIL)
      : mapBodyFont(bodyHint),
  };
}

// The @font-face / stylesheet load list for a brand's saved fonts, in the shape
// the send twins consume. Empty when the brand saved no usable fonts (→ no font
// loading injected → byte-identical to the pre-saved-fonts render).
function resolveFontFaces(brand: any): EmailBrandFontFace[] {
  const ds = brand?.brandDesignSystem ?? {};
  const roles = selectBrandFontRoles(ds.fonts);
  const { googleFamilies, uploadedFonts } = collectBrandFontLoads(roles);
  const faces: EmailBrandFontFace[] = [];
  for (const family of googleFamilies) {
    faces.push({ family, source: "google", url: null, format: null });
  }
  for (const f of uploadedFonts) {
    faces.push({
      family: brandFontFamilyName(f),
      source: "upload",
      url: f.url,
      format: brandFontFormat(f),
    });
  }
  return faces;
}

// ── Button derivation ────────────────────────────────────────────────────────
function resolveButton(brand: any, colors: EmailBrandColors): EmailBrandButton {
  const ds = brand?.brandDesignSystem ?? {};
  const desc = String(ds?.componentStyle?.buttons ?? "").toLowerCase();
  let borderRadius = DEFAULT_BUTTON_RADIUS;
  if (desc.includes("pill") || desc.includes("round") || desc.includes("fully rounded")) {
    borderRadius = "9999px";
  } else if (desc.includes("square") || desc.includes("sharp") || desc.includes("no radius")) {
    borderRadius = "0px";
  }
  // Solid accent fill with a contrast-correct label — overridable by future data.
  return {
    backgroundColor: colors.accent,
    color: colors.onAccent,
    borderRadius,
    border: null,
    textTransform: null,
    fontFamily: null,
  };
}

// ── Surface derivation ───────────────────────────────────────────────────────
// Build the section-surface palette from the resolved brand colors. Each surface
// pairs a background with WCAG-legible text/muted/border colors so any block can
// paint a brand-colored band, a soft tint, or a dark contrast section without the
// renderers hardcoding anything. Deterministic from `colors`, so the preview and
// the MJML send compute identical surfaces.
function makeSurface(bg: string): EmailBrandSurface {
  const text = readableOn(bg);
  const dark = text === "#FFFFFF";
  return {
    bg,
    text,
    // Muted text = pull the readable text ~32% toward the bg (still legible).
    muted: mix(text, bg, dark ? 0.28 : 0.34),
    // Hairline = a faint line that reads on the surface.
    border: dark ? lighten(bg, 0.18) : darken(bg, 0.1),
    dark,
  };
}

function resolveSurfaces(colors: EmailBrandColors): EmailBrandSurfaces {
  // Page canvas: prefer the brand's soft background, but never a flat pure white
  // — if it's essentially white, derive a faint brand-tinted paper from primary.
  const bgIsFlatWhite = luminance(colors.background) > 0.96;
  const page = bgIsFlatWhite ? mix(colors.primary, "#ffffff", 0.95) : colors.background;
  // Quiet brand wash for "color band" sections that stay light/readable.
  const tinted = lighten(colors.primary, 0.88);
  // Dark contrast band: the darker of primary/footer, driven dark enough that
  // white text clears AA.
  const darkBase =
    luminance(colors.footer) <= luminance(colors.primary) ? colors.footer : colors.primary;
  const dark = ensureDarkBg(darkBase);
  return {
    page: makeSurface(page),
    light: makeSurface(colors.card),
    tinted: makeSurface(tinted),
    dark: makeSurface(dark),
    accent: makeSurface(colors.accent),
  };
}

// ── Brand Styling layer application ──────────────────────────────────────────
// The BrandStyleDirection (server-persisted, brand-sourced) may re-dress the
// resolved bundle using BOUNDED tokens only — never layout. Applied by the ONE
// shared resolver so both render twins (preview + MJML/Satori) stay in lockstep.
// A null/absent direction is a NO-OP → byte-identical to the pre-Brand-Styling
// render. Because the deterministic baseline round-trips the brand's own CTA
// shape/color role, a `deterministic`-source direction also renders identically;
// only an LLM role/shape change alters the button.
function ctaRadiusFromShape(shape: string): string {
  if (shape === "pill") return "9999px";
  if (shape === "rectangle") return "0px";
  return "8px"; // rounded
}

function colorForRole(
  role: string,
  colors: EmailBrandColors,
  secondaryHint?: string,
): string {
  switch (role) {
    case "primary":
      return colors.primary;
    case "dark":
      return colors.footer;
    case "secondary":
      return isHexColor(secondaryHint) ? normalizeHex(secondaryHint as string) : colors.accent;
    case "accent":
    default:
      return colors.accent;
  }
}

// Resolve a typography hint to a loadable CSS font family. The deterministic
// baseline mirrors the brand's OWN resolved family (already a full CSS stack),
// so a concrete stack (contains a comma or quote) is used verbatim → a
// `deterministic`-source direction round-trips the exact same font. A bare
// descriptive hint (e.g. "elegant serif") is mapped the same way the brand's
// own typography direction is — never raw CSS.
function fontFromHint(
  hint: unknown,
  fallback: string,
  map: (desc: string) => string,
): string {
  if (typeof hint !== "string" || !hint.trim()) return fallback;
  const h = hint.trim();
  if (h.includes(",") || h.includes('"')) return h;
  return map(h);
}

// `direction` is typed loosely (any) to avoid a shared↔schema import cycle; the
// fields consumed here are the bounded EmailBrandStyleDirection tokens. The
// direction re-dresses the resolved bundle with BOUNDED tokens only — color
// roles, typography families, CTA shape/role and the surfaces derived from them
// — never layout. Because the deterministic baseline mirrors the brand's own
// resolved colors/fonts/CTA, a `deterministic`-source direction rebuilds a
// byte-identical bundle; only an LLM role/shape/font change alters the render.
export function applyBrandStyleDirection(
  style: EmailBrandStyle,
  direction: any,
): EmailBrandStyle {
  if (
    !direction ||
    typeof direction !== "object" ||
    !direction.colorSystem ||
    !direction.ctaSystem
  ) {
    return style;
  }
  const cs = direction.colorSystem;
  // 1. Re-dress the brand color roles from the direction (bounded to the brand
  //    palette by the director). onPrimary/onAccent/onFooter are recomputed the
  //    same way resolveColors does, so an unchanged palette round-trips exactly.
  const colors: EmailBrandColors = {
    ...style.colors,
    primary: isHexColor(cs.primary) ? normalizeHex(cs.primary) : style.colors.primary,
    accent: isHexColor(cs.accent) ? normalizeHex(cs.accent) : style.colors.accent,
    background: isHexColor(cs.background)
      ? normalizeHex(cs.background)
      : style.colors.background,
    footer: isHexColor(cs.dark) ? normalizeHex(cs.dark) : style.colors.footer,
  };
  colors.onPrimary = readableOn(colors.primary);
  colors.onAccent = readableOn(colors.accent);
  colors.onFooter = readableOn(colors.footer);

  // 2. Typography — map the direction's font hints to loadable families.
  const ts = direction.typographySystem ?? {};
  const fonts: EmailBrandFonts = {
    heading: fontFromHint(ts.headlineFont, style.fonts.heading, mapHeadingFont),
    body: fontFromHint(ts.bodyFont, style.fonts.body, mapBodyFont),
  };

  // 3. CTA button — color role + shape, layered over the re-dressed colors.
  const ctaColor = colorForRole(
    String(direction.ctaSystem.colorRole ?? "accent"),
    colors,
    cs.secondary,
  );
  const button: EmailBrandButton = {
    backgroundColor: ctaColor,
    color: readableOn(ctaColor),
    borderRadius: ctaRadiusFromShape(String(direction.ctaSystem.shape ?? "rounded")),
    border: null,
    textTransform: null,
    fontFamily: null,
  };

  // 4. Surfaces recomputed from the re-dressed colors so section bands, panels
  //    and dividers reflect the direction's color roles across the whole email,
  //    not just the CTA. resolveSurfaces is deterministic in `colors`, so an
  //    unchanged palette yields byte-identical surfaces.
  const surfaces = resolveSurfaces(colors);

  return { ...style, colors, fonts, button, surfaces };
}

// ── Public resolver ──────────────────────────────────────────────────────────
// `direction` (optional) is the persisted BrandStyleDirection. When null/absent
// the output is byte-identical to before the Brand Styling layer existed.
export function resolveEmailBrandStyle(
  brand: any,
  direction?: any,
): EmailBrandStyle {
  const colors = resolveColors(brand);
  const logoUrl =
    (typeof brand?.logoUrl === "string" && brand.logoUrl.trim()) ||
    (typeof brand?.adgenLogoUrl === "string" && brand.adgenLogoUrl.trim()) ||
    null;
  const style: EmailBrandStyle = {
    brandName: (typeof brand?.name === "string" && brand.name.trim()) || "Your brand",
    logoUrl,
    colors,
    fonts: resolveFonts(brand),
    button: resolveButton(brand, colors),
    surfaces: resolveSurfaces(colors),
    fontFaces: resolveFontFaces(brand),
  };
  const resolved = direction ? applyBrandStyleDirection(style, direction) : style;
  // Task #1233 — the brand's EXPLICIT CTA/button style (Design System →
  // ctaStyle) outranks both the palette-derived button AND any LLM Brand
  // Styling direction: a user-chosen setting is authoritative. Applied here in
  // the ONE shared resolver so every render twin (editor preview, captured/
  // Satori blocks, MJML send, polish-prompt descriptions) stays in lockstep.
  // resolveBrandCtaStyle is null for unset/disabled brands → byte-identical.
  // SCOPE: brand CTA style is ads-only for now (BRAND_CTA_SURFACES.email is
  // false) — emails render exactly as if the brand had no CTA style set.
  const brandCta = resolveBrandCtaStyleForSurface("email", brand?.brandDesignSystem);
  if (brandCta) {
    const outline = brandCta.fillStyle === "outline";
    resolved.button = {
      // Outline fill = transparent interior + a 2px border in the fill color;
      // solid keeps the exact pre-fillStyle shape (border stays null).
      backgroundColor: outline ? "transparent" : brandCta.fillColor,
      // brandCta.textColor is the single source of truth for CTA text: either
      // the user's explicit pick or the shared autoCtaTextColor contrast (the
      // SAME value email-satori's hero lockup uses; for outline the auto text
      // is the fill color itself). Do NOT substitute the local readableOn here
      // — its 0.5 luminance threshold differs from the shared 0.55 one, which
      // would let Satori/captured blocks and MJML send disagree for fills in
      // the 0.50–0.55 band.
      color: brandCta.textColor,
      borderRadius: brandCta.shape
        ? ctaShapeRadiusCss(brandCta.shape)
        : resolved.button.borderRadius,
      border: outline ? `2px solid ${brandCta.fillColor}` : null,
      textTransform: brandCta.allCaps ? "uppercase" : null,
      fontFamily: brandCta.font ? brandCta.font.cssStack : null,
    };
    // Make the CTA label font loadable by every render twin (preview CSS,
    // Satori, MJML head) — appended to the same fontFaces list the heading/
    // body role fonts use, deduped by family so a font that already serves a
    // role isn't declared twice.
    if (brandCta.font) {
      const faces = resolved.fontFaces ?? [];
      if (!faces.some((f) => f.family === brandCta.font!.cssFamily)) {
        resolved.fontFaces = [
          ...faces,
          {
            family: brandCta.font.cssFamily,
            source: brandCta.font.source,
            url: brandCta.font.source === "upload" ? brandCta.font.fileUrl : null,
            format: brandCta.font.source === "upload" ? brandCta.font.fileFormat : null,
          },
        ];
      }
    }
  }
  return resolved;
}

// ── Shared type scale ────────────────────────────────────────────────────────
// The ONE size hierarchy every Email Express renderer draws from — the client
// preview (email-render.tsx / email-preset-blocks.tsx), the MJML send
// (server/services/email-mjml.ts), and the Satori-rasterized bands
// (server/services/email-satori.ts). Values are CSS px at the shared 600px
// design width, so a step renders the same optical size on every surface.
// Hierarchy: heroDisplay > heroHeadline > sectionHeading > leadEmphasis > lead
// > bodyLarge/pullQuote > body > cta > label > eyebrow/footerText > finePrint.
// Renderers must reference these steps instead of hardcoding pixel sizes so a
// rasterized block sitting next to a live-text block reads as ONE email.
export const EMAIL_TYPE_SCALE = {
  /** Display hero headline (photo-forward hero, baked hero lockup). */
  heroDisplay: 40,
  /** Standard hero headline in a band / over a photo band. */
  heroHeadline: 32,
  /** Understated compact hero caption (full-bleed-minimal). */
  heroCompact: 24,
  /** Brand wordmark in a hero lockup. */
  wordmarkLarge: 24,
  /** Brand wordmark in the footer sign-off. */
  wordmarkFooter: 20,
  /** Standalone section / CTA-band heading. */
  sectionHeading: 26,
  /** Emphasized lead paragraph (editorial / pull-quote lead). */
  leadEmphasis: 21,
  /** Standard lead (first) body paragraph. */
  lead: 17,
  /** Large body text (facts, pull-quote follow paragraphs). */
  bodyLarge: 16,
  /** Standalone pull-quote line. */
  pullQuote: 16,
  /** Standard body copy. */
  body: 15,
  /** Emphasized feature/quote text (hero-tier selling point, big review quote). */
  bodyEmphasis: 18,
  /** Large numeric/glyph accent (offer code pill, "?" ring, check badges). */
  statBadge: 20,
  /** Compact supporting copy (comparison cells, selling-point grid text). */
  bodySmall: 14,
  /** Tertiary meta text (table headers, review meta, offer description). */
  caption: 13,
  /** CTA button label. */
  cta: 14,
  /** Small labels / kickers that must stay readable. */
  label: 12,
  /** Uppercase eyebrow kicker / sale pill / section intro kicker. */
  eyebrow: 11,
  /** Footer links, address, social. */
  footerText: 11,
  /** Legal fine print / copyright / badge labels. */
  finePrint: 10,
} as const;

// ── Image-block sizing contract (image_only / image_discount) ────────────────
// The editor preview (and therefore every captured send) renders these blocks
// full-width at the 600px design width with a max height and center-crop
// (object-fit: cover). The MJML fallback renderer MUST apply the same contract
// so a tall/portrait chosen or uploaded image can't show cropped in the preview
// but full-height in the sent email (Task #2286).
export const EMAIL_IMAGE_BLOCK_MAX_HEIGHTS = {
  image_only: 480,
  image_discount: 380,
} as const;

// A selected Body Copy image always uses one fixed-height, center-cropped band
// on every surface. Unlike the optional image-only blocks, this deliberately
// does not depend on a server-side dimension probe: product photos may live on
// hosts the SSRF guard cannot fetch, and a fixed crop keeps wide and portrait
// sources identical in browser capture and MJML fallback delivery.
export const EMAIL_BODY_IMAGE_HEIGHT = 320;

export type EmailImageBlockCropPlan =
  | { mode: "natural" }
  | { mode: "cover"; height: number };

// Pure crop plan for an image block: "natural" when the image (scaled to the
// design width) fits under the cap — render it untouched, exactly like today —
// or "cover" with the capped pixel height when it would overflow, meaning both
// renderers must center-crop to that height. Unknown dimensions → "natural"
// (fail-open to the historical unconstrained render).
export function emailImageBlockCropPlan(
  type: keyof typeof EMAIL_IMAGE_BLOCK_MAX_HEIGHTS,
  dims: { width: number; height: number } | null | undefined,
  designWidth = 600,
): EmailImageBlockCropPlan {
  const cap = EMAIL_IMAGE_BLOCK_MAX_HEIGHTS[type];
  if (!dims || !(dims.width > 0) || !(dims.height > 0)) return { mode: "natural" };
  const scaledHeight = (dims.height / dims.width) * designWidth;
  return scaledHeight > cap ? { mode: "cover", height: cap } : { mode: "natural" };
}

// Google Fonts stylesheet covering every family the resolver can map to.
export const EMAIL_FONT_LINK_HREF =
  "https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@400;500;600;700;800&family=Playfair+Display:wght@500;600;700;800&family=Montserrat:wght@400;500;600;700;800&family=Lora:wght@400;500;600;700&display=swap";

// The brand-saved Google families in a resolved style (for extending the
// preview/send stylesheet beyond the built-in EMAIL_FONT_LINK_HREF families).
export function emailBrandGoogleFamilies(style: EmailBrandStyle): string[] {
  return (style.fontFaces ?? [])
    .filter((f) => f.source === "google" && f.family)
    .map((f) => f.family);
}

// @font-face rules for a resolved style's uploaded brand fonts. `resolveUrl`
// lets the caller proxy the R2 URL in the browser (CORS) while the server uses
// the raw URL. Empty string when there are no uploaded faces.
export function emailBrandFontFaceCss(
  style: EmailBrandStyle,
  resolveUrl: (url: string) => string = (u) => u,
): string {
  return (style.fontFaces ?? [])
    .filter((f) => f.source === "upload" && f.url)
    .map(
      (f) =>
        `@font-face { font-family: "${f.family}"; src: url("${resolveUrl(
          f.url as string,
        )}")${f.format ? ` format("${f.format}")` : ""}; font-display: swap; }`,
    )
    .join("\n");
}
